import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { login as loginApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LoadingScreen from '../components/LoadingScreen';

// ---------------------------------------------------------------------------
// SsoTicketPage  (route: /sso-ticket)
//
// Entry point used by external apps (e.g. the Zyra ERP) to let a user raise a
// support ticket without a manual login. The ERP "Raise ITSM Ticket" button
// opens:   /sso-ticket?subject=<error message>
//
// This page signs in with the dedicated ERP ticket-raiser account, then
// forwards to the New Ticket form carrying the subject through, so the user
// lands straight on the pre-filled form.
//
// NOTE: this is a shared low-effort raiser identity for an internal/test tool.
// For production, swap to a dedicated low-privilege account (see README) so a
// leaked credential can only ever create tickets.
// ---------------------------------------------------------------------------

const RAISER_EMAIL = 'admin@itsm.com';
const RAISER_PASSWORD = 'Admin@123';

export default function SsoTicketPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loginUser } = useAuth();
  const [failed, setFailed] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    // Forward every param we were given (subject, requestorEmail, requestorName)
    // straight through to the New Ticket form.
    const qs = searchParams.toString();
    const dest = `/tickets/new${qs ? `?${qs}` : ''}`;

    // Already authenticated in this tab? Skip the login and go straight in.
    if (user) {
      navigate(dest, { replace: true });
      return;
    }

    loginApi({ email: RAISER_EMAIL, password: RAISER_PASSWORD })
      .then((res) => {
        loginUser(res.data.token, res.data.user);
        navigate(dest, { replace: true });
      })
      .catch(() => setFailed(true));
  }, [user, searchParams, loginUser, navigate]);

  if (failed) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, fontFamily: 'system-ui, sans-serif', padding: 24, textAlign: 'center' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#e11d48' }}>Could not open the ticket form</h2>
        <p style={{ color: '#64748b', maxWidth: 420 }}>
          Auto sign-in to the ITSM tool failed. Please log in manually and create the ticket.
        </p>
        <a href="/login" style={{ color: '#4f46e5', fontWeight: 600 }}>Go to login</a>
      </div>
    );
  }

  return <LoadingScreen />;
}
